"use client";

import { useEffect } from "react";
import { Button } from "@/components/button";
// global-error REPLACES the root layout on a crash, so none of the root layout's
// CSS (fonts/tokens/globals/primitives) is applied — import what this page needs
// itself. The active brand's tokens define the `:root` custom properties the
// primitive + local styles read; a white-label rebrand touches this import too.
import "../../design/tokens/dist/plotzio/tokens.css";
import "@/components/primitives.css";
import "./global-error.css";

/**
 * Root error boundary (MAP-192). App Router renders this when an error escapes to
 * the root — either a crash in the root layout/template itself, or any error in a
 * segment with no closer `error.tsx`. It's the client-side catch-all for React
 * render crashes; unhandled browser errors/rejections outside render are caught
 * by posthog-js `capture_exceptions`, and server errors by `onRequestError`
 * (src/instrumentation.ts).
 *
 * Only fires in production (dev shows the error overlay). Must render its own
 * <html>/<body> because it stands in for the root layout.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // A `digest` means Next hashed a SERVER-thrown error and forwarded it to the
    // client boundary — onRequestError already captured it server-side, so
    // capturing again here would double-count. Report only client-origin errors
    // (no digest): the render crashes that never touch the server.
    if (error.digest) return;
    // Dynamic import keeps posthog-js code-split out of the shared/embed bundle
    // (FAIL-448) — a static import here would bundle it into every route.
    void import("@/lib/analytics/client").then((m) => m.captureClientException(error));
  }, [error]);

  return (
    // data-theme is the live brand: tokens.css scopes vars to :root AND
    // :root[data-theme="plotzio"]; either resolves, this is explicit.
    <html lang="en" data-theme="plotzio">
      <body>
        <main className="pz-globalerror">
          <div className="pz-globalerror__inner">
            <h1 className="pz-globalerror__title">Something went wrong</h1>
            <p className="pz-globalerror__lede">
              This page ran into an unexpected error. Try again, or go back to the homepage.
            </p>
            <div className="pz-globalerror__actions">
              <Button variant="primary" size="lg" onClick={() => reset()}>
                Try again
              </Button>
              {/* Plain <a>, not next/link, ON PURPOSE: this renders when the root
                  layout has crashed, so a full-document reload is the reliable way
                  back — client-side Link nav depends on router state that may be
                  broken here. */}
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
              <a className="pz-globalerror__home" href="/">
                Go to homepage
              </a>
            </div>
          </div>
        </main>
      </body>
    </html>
  );
}
